import { useEffect } from 'react'
import { Box } from '@mui/material'
import PreviewCard from 'components/PreviewCard'
import { useDispatch, useSelector } from 'react-redux'
import { getLanguages } from 'store/lookup'
import { saveSequences } from 'store/academic/parent/contact'
import { useTranslation } from 'react-i18next'
import GuardianCard from './GuardianCard'
import AdditionalContactItem from './AdditionalContactItem'

const MAX_OTHER_CONTACTS = 5

export default function ContactsList({ data, memberId, onEdit, onEditOwn, onAdd, onReordered }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const languages = useSelector(state => state.lookup.languages || [])

  useEffect(() => {
    if (!languages?.length) {
      dispatch(getLanguages())
    }
  }, [dispatch])

  const guardianContacts = data?.filter(item => item?.isGuardian === true) || []
  const otherContacts = data?.filter(item => item?.isGuardian !== true) || []

  while (guardianContacts.length < 2) guardianContacts.push({ isGuardian: true })

  const maxSeq = (data || []).reduce((max, item) => Math.max(item.seq || 0, max), 0)

  const getLanguageName = item => {
    const preferredLanguage = item?.contact?.preferredLanguage
    if (!preferredLanguage) return ''

    return languages?.find(lang => lang?.id === preferredLanguage)?.name || preferredLanguage || ''
  }

  const movedItemData = { oldIndex: 0, newIndex: 0, end: false, ongoing: false, id: -1 }

  const setMovedItemData = (oldIndex, newIndex, end, ongoing, id) => {
    if (oldIndex >= 0) {
      movedItemData.oldIndex = oldIndex
      movedItemData.id = id
    }
    if (newIndex >= 0) {
      movedItemData.newIndex = newIndex
    }
    if (!end && ongoing) {
      movedItemData.ongoing = true
      movedItemData.end = false
    }
    if (end && !ongoing) {
      movedItemData.end = true
      movedItemData.ongoing = false
      reOrder()
    }
  }

  const reOrder = async () => {
    const temp = otherContacts.filter(item => item.id !== movedItemData.id).map(item => ({ id: item.id }))
    temp.splice(movedItemData.newIndex, 0, { id: movedItemData.id })

    const newSequences = temp.map((item, index) => ({ ...item, seq: index + 1 }))

    const res = await dispatch(saveSequences({ memberId, newSequences }))
    if (res.payload) onReordered?.()

    movedItemData.oldIndex = 0
    movedItemData.newIndex = 0
    movedItemData.end = false
    movedItemData.ongoing = false
    movedItemData.id = -1
  }

  return (
    <Box sx={{ mt: 1 }}>
      {guardianContacts.map((item, index) => (
        <Box sx={{ mx: 3, my: 2 }} key={item?.id || index}>
          <GuardianCard
            data={item}
            title={index === 0 ? t('Primary Parent/Guardian') : t('Secondary Parent/Guardian')}
            languageName={getLanguageName(item)}
            onMore={() => onEditOwn({ contact: item, isPrimary: index === 0 })}
          />
        </Box>
      ))}

      <Box sx={{ m: 3, my: 2 }}>
        <PreviewCard
          title={t('Additional Contacts')}
          size="large"
          otherButton={{
            buttonLabel: t('Add Contact'),
            buttonClick: () => onAdd(maxSeq + 1),
            buttonDisabled: otherContacts.length >= MAX_OTHER_CONTACTS,
          }}
        >
          <Box sx={{ width: '100%' }}>
            {otherContacts.length > 0 ? (
              otherContacts.map((item, index) => (
                <AdditionalContactItem
                  key={item.id}
                  item={item}
                  index={index}
                  itemCount={otherContacts.length}
                  languageName={getLanguageName(item)}
                  onEdit={onEdit}
                  setMovedItemData={setMovedItemData}
                />
              ))
            ) : (
              <Box sx={{ m: 3 }}>{t('No Records')}</Box>
            )}
          </Box>
        </PreviewCard>
      </Box>
    </Box>
  )
}
